import FormattedCurrency from '@otovo/shared/components/Currency/FormattedCurrency';
import useLocalizedPath from '@otovo/shared/components/Navigation/useLocalizedPath';
import PortableText, {
  PortablePTag,
} from '@otovo/shared/components/Sanity/PortableTextComponents';
import useHasMounted from '@otovo/shared/hooks/useHasMounted';
import { getImageUrl } from '@otovo/shared/lib/sanity/imageUrlUtils';
import {
  Sanity$PaymentComparison,
  Sanity$PaymentComparisonIncludedEntry,
} from '@otovo/shared/types/sanityTypes';
import { useRouter } from 'next/router';
import CTAButton from '../../v2/CTAButton';
import CTALink from '../../v2/CTALink';
import EditIcon from '../../v2/icons/Edit';
import SearchIcon from '../../v2/icons/Search';
import ShieldGoodIcon from '../../v2/icons/ShieldGood';
type Props = {
  content: Sanity$PaymentComparison;
};

function assertExhaustiveCheck(type: never) {
  throw new Error(`Unhandled case: ${type}`);
}

function getIcon(entry: Sanity$PaymentComparisonIncludedEntry) {
  const id = entry.id;
  const props = {
    width: 32,
    height: 32,
  };
  switch (id) {
    case 'administrativeHandling':
      return <EditIcon {...props} />;
    case 'digitalInspection':
      return <SearchIcon {...props} />;
    case 'guaranteeAndSupport':
      return <ShieldGoodIcon {...props} />;
    default:
      assertExhaustiveCheck(id);
  }
}

const PaymentComparison = ({ content }: Props) => {
  const hasMounted = useHasMounted();
  const router = useRouter();

  const localizedGetAQuotePath = useLocalizedPath('/get-a-quote');
  const localizedPricingPath = useLocalizedPath('/pricing');
  const isOnPricingPage = router.pathname === '/pricing';

  const backgroundImageUrl = getImageUrl(content.image);
  return (
    <>
      <div
        className="color-scheme-white relative bg-surface"
        data-testid="payment-comparison"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImageUrl})`,
          }}
        />

        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(270deg, rgba(27, 36, 56, 0.00) 0%, rgba(27, 36, 56, 0.60) 37.5%, rgba(27, 36, 56, 0.90) 51.69%, #1B2438 65.87%), linear-gradient(0deg, rgba(242, 115, 80, 0.05) 0%, rgba(242, 115, 80, 0.05) 100%)',
          }}
        />

        <div className="color-scheme-dark relative mx-auto max-w-8xl px-4 py-16 md:px-6 md:py-20 lg:px-16 lg:py-32">
          <div className="grid grid-cols-12 gap-6">
            {/* First 4 cols: heading and description */}
            <div className="col-span-12 mb-8 space-y-4 md:col-span-4 md:mb-0 lg:pr-10">
              <h2 className="text-2xl font-semibold text-pretty text-heading md:text-3xl lg:text-4xl">
                {content.heading}
              </h2>
              <PortablePTag
                className="text-base text-subheading md:text-xl"
                value={content.description}
              />

              <div className="my-8">
                <CTAButton href={`${localizedGetAQuotePath}/`}>
                  {content.quoteCtaTitle}
                </CTAButton>
              </div>

              {!isOnPricingPage && (
                <div>
                  <CTALink href={`${localizedPricingPath}/`}>
                    {content.pricingCtaTitle}
                  </CTALink>
                </div>
              )}
            </div>

            {/* Payment option cards */}
            {content.paymentOptions.map((option) => (
              <div
                key={option._key}
                className="color-scheme-light col-span-12 flex flex-col rounded-lg border border-grey-20 bg-white p-6 shadow-lg md:col-span-4"
              >
                {/* Header */}
                <div className="mb-6">
                  <h3 className="mb-2 text-base font-semibold text-heading">
                    {option.title}
                  </h3>
                  <div className="mb-4">
                    {hasMounted && (
                      <span className="text-2xl font-bold text-heading lg:text-3xl">
                        {option.fromPrice.prefix &&
                          `${option.fromPrice.prefix} `}
                        <FormattedCurrency
                          value={option.fromPrice.amount}
                          perUnit={
                            option.fromPrice.perMonth ? 'month' : undefined
                          }
                        />
                      </span>
                    )}
                  </div>
                  <PortablePTag
                    className="text-sm text-subheading"
                    value={option.description}
                  />
                </div>

                {/* Value props */}
                <div className="mb-4">
                  <PortableText
                    value={option.valueProps}
                    components={{
                      list: {
                        bullet: ({ children }) => (
                          <ul className="mb-6 space-y-3 text-body">
                            {children}
                          </ul>
                        ),
                      },
                      listItem: {
                        bullet: ({ children }) => (
                          <li className="flex items-start space-x-2">
                            <div className="mt-1 mr-1 h-5 w-5 flex-shrink-0">
                              <svg
                                className="h-4 w-4 text-green-50"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                            {children}
                          </li>
                        ),
                      },
                    }}
                  />
                </div>
                <p className="mt-auto block text-xs text-muted">
                  {option.disclaimerText}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {content.alwaysIncludedInThePrice?.label && (
        <div
          className="color-scheme-dark bg-surface"
          data-test-id="always-included-in-the-price"
        >
          <div className="mx-auto max-w-8xl grid-cols-12 items-center gap-8 px-4 py-20 md:px-6 xl:grid xl:px-16">
            <div className="col-span-5 mb-8 text-center xl:mb-0 xl:text-left">
              <h3 className="text-2xl font-semibold text-pretty text-heading md:text-3xl lg:text-4xl">
                {content.alwaysIncludedInThePrice.label}
              </h3>
            </div>

            <ul className="col-span-7 flex flex-col justify-evenly gap-y-10 md:flex-row">
              {content.alwaysIncludedInThePrice.entries?.map((entry) => (
                <li className="mb-0 text-subheading" key={entry.id}>
                  <div className="flex flex-col items-center text-center lg:px-10">
                    <div className="mb-2">{getIcon(entry)}</div>
                    <span className="block text-pretty md:max-w-[16ch]">
                      {entry.label}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentComparison;
